/* eslint-disable no-unused-vars */
import React, { useMemo, useState } from 'react';
import {
  Button,
  Col,
  Input,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment';
import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons';
import OtaFormModal from './ota-form-modal';
import FillBundleModal from './fill-bundle-modal';
import { getUserObj, _ } from '../../../../utils';
import legacyOtaServices from '../../../../services/legacy-ota.services';
import { Panel, PanelBody, PanelHeader } from '../../../util/panel';

const { Text } = Typography;

const environmentColors = {
  dev: 'blue',
  uat: 'orange',
  prod: 'red',
};

const environmentOptions = [
  { value: 'all', label: 'ทุก Environment' },
  { value: 'dev', label: 'dev' },
  // { value: 'uat', label: 'uat' },
  { value: 'prod', label: 'prod' },
];

const statusOptions = [
  { value: 'all', label: 'ทุกสถานะ' },
  { value: 'on', label: 'เปิดใช้งาน (ON)' },
  { value: 'off', label: 'ปิดใช้งาน (OFF)' },
];

const LegacyOtaPage = () => {
  const [environmentFilter, setEnvironmentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formModalVisible, setFormModalVisible] = useState(false);
  const [fillTarget, setFillTarget] = useState(null);
  const [prodConfirmTarget, setProdConfirmTarget] = useState(null);
  const [prodConfirmText, setProdConfirmText] = useState('');

  const user = getUserObj();

  const { data, isLoading, refetch } = legacyOtaServices.useQueryGetOtaVersions(
    {
      environment: environmentFilter,
      active:
        statusFilter === 'all'
          ? undefined
          : statusFilter === 'on'
            ? true
            : false,
    },
  );

  const { mutate: toggleActive, isLoading: isToggling } =
    legacyOtaServices.useMutationToggleOtaActive(
      () => refetch(),
      (error) => {
        Modal.error({
          title: 'เปลี่ยนสถานะไม่สำเร็จ',
          content: error?.message || error,
          okText: 'ตกลง',
          zIndex: 1080,
        });
      },
    );

  const { mutate: deleteOta } = legacyOtaServices.useMutationDeleteOta(
    () => refetch(),
    (error) => {
      Modal.error({
        title: 'ลบไม่สำเร็จ',
        content: error?.message || error,
        okText: 'ตกลง',
        zIndex: 1080,
      });
    },
  );

  const versions = data?.data || [];

  // เตือนเมื่อเปิด ON ซ้ำ target เดียวกัน — server จะเลือก version สูงสุดให้
  const activeCountByTarget = useMemo(() => {
    return _.countBy(
      versions.filter((v) => v.active),
      (v) => `${v.environment}|${v.targetAppVersion}`,
    );
  }, [versions]);

  const confirmToggle = (record, nextActive) => {
    if (!nextActive) {
      Modal.confirm({
        title: `ปิดใช้งาน version ${record.version}?`,
        content: record.androidBundleUrl
          ? 'version รองที่ยังเปิดอยู่ (ถ้ามี) จะถูกเลือกแทนอัตโนมัติ'
          : 'version นี้จะไม่ถูกส่งให้ app จนกว่าจะเปิดอีกครั้ง',
        okText: 'ปิดใช้งาน',
        okType: 'danger',
        cancelText: 'ยกเลิก',
        zIndex: 1080,
        onOk: () => toggleActive({ id: record.id, active: false }),
      });
      return;
    }

    const duplicateCount =
      activeCountByTarget[`${record.environment}|${record.targetAppVersion}`] ||
      0;
    const warning =
      duplicateCount > 0
        ? `\n\nคำเตือน: มีอีก ${duplicateCount} version ที่เปิดอยู่สำหรับ ${record.targetAppVersion} — server จะเลือก version สูงสุดให้ app เสมอ`
        : '';

    // prod ต้องพิมพ์ CONFIRM ก่อนเปิด — กระทบ app ทั้ง production
    if (record.environment === 'prod') {
      setProdConfirmText('');
      setProdConfirmTarget({ record, warning });
      return;
    }

    Modal.confirm({
      title: `เปิดใช้งาน version ${record.version} (${record.environment})?`,
      content: `app ${record.targetAppVersion} build ${record.minBuildNumber}${
        record.maxBuildNumber ? `-${record.maxBuildNumber}` : '+'
      } จะได้รับ OTA v${record.version}${warning}`,
      okText: 'เปิดใช้งาน',
      okType: 'primary',
      cancelText: 'ยกเลิก',
      zIndex: 1080,
      onOk: () => toggleActive({ id: record.id, active: true }),
    });
  };

  const confirmDelete = (record) => {
    Modal.confirm({
      title: `ลบ version ${record.version} (${record.environment})?`,
      content: record.active
        ? 'version นี้กำลังเปิดใช้งานอยู่ — ต้องปิดก่อนจึงจะลบได้ หรือกด "ลบแบบบังคับ" เพื่อปิดและลบพร้อมกัน'
        : 'ลบแล้ว version นี้จะหายจากประวัติ (bundle zip บน storage ยังอยู่)',
      okText: 'ลบ',
      okType: 'danger',
      cancelText: 'ยกเลิก',
      zIndex: 1080,
      onOk: () =>
        record.active
          ? new Promise((resolve, reject) => {
              Modal.confirm({
                title: 'ลบแบบบังคับ (force)?',
                content: 'จะปิดใช้งานและลบ version นี้ทันที',
                okText: 'ลบแบบบังคับ',
                okType: 'danger',
                cancelText: 'ยกเลิก',
                zIndex: 1081,
                onOk: () => {
                  deleteOta({ id: record.id, force: true });
                  resolve();
                },
                onCancel: () => resolve(),
              });
            })
          : deleteOta({ id: record.id }),
    });
  };

  const columns = [
    {
      title: 'Build Version',
      dataIndex: 'version',
      width: 110,
      render: (value) => <Text strong>{value}</Text>,
    },
    {
      title: 'Environment',
      dataIndex: 'environment',
      width: 110,
      render: (value) => <Tag color={environmentColors[value]}>{value}</Tag>,
    },
    {
      title: 'Target App',
      dataIndex: 'targetAppVersion',
      width: 110,
    },
    {
      title: 'Build Range',
      width: 130,
      render: (_, record) =>
        `${record.minBuildNumber}${
          record.maxBuildNumber ? ` - ${record.maxBuildNumber}` : '+'
        }`,
    },
    {
      title: 'แพ็กเกจ',
      width: 130,
      render: (_, record) => (
        <>
          {record.androidBundleUrl ? (
            <a
              href={record.androidBundleUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CheckCircleFilled style={{ color: '#52c41a' }} /> Android
            </a>
          ) : (
            <Text type="secondary">
              <CloseCircleFilled style={{ color: '#ff4d4f' }} /> Android
            </Text>
          )}
          <br />
          {record.iosBundleUrl ? (
            <a
              href={record.iosBundleUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <CheckCircleFilled style={{ color: '#52c41a' }} /> iOS
            </a>
          ) : (
            <Text type="secondary">
              <CloseCircleFilled style={{ color: '#ff4d4f' }} /> iOS
            </Text>
          )}
        </>
      ),
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'description',
      ellipsis: true,
      render: (value) => value || '-',
    },
    {
      title: 'สถานะ',
      dataIndex: 'active',
      width: 90,
      render: (value, record) => (
        <Switch
          checked={value}
          loading={isToggling}
          onChange={(next) => confirmToggle(record, next)}
        />
      ),
    },
    {
      title: 'ผู้สร้าง',
      dataIndex: 'createdBy',
      width: 110,
      render: (value) => value || '-',
    },
    {
      title: 'เปิดเมื่อ',
      dataIndex: 'activatedAt',
      width: 140,
      render: (value) =>
        value ? moment(value).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'การกระทำ',
      width: 220,
      render: (_, record) => (
        <>
          {!record.androidBundleUrl && (
            <Button
              size="small"
              onClick={() => setFillTarget(record)}
              style={{ marginRight: 4 }}
            >
              อัปโหลด Android
            </Button>
          )}
          {!record.iosBundleUrl && (
            <Button
              size="small"
              onClick={() => setFillTarget(record)}
              style={{ marginRight: 4 }}
            >
              อัปโหลด iOS
            </Button>
          )}
          <Button size="small" danger onClick={() => confirmDelete(record)}>
            ลบ
          </Button>
        </>
      ),
    },
  ];

  return (
    <Panel>
      <PanelHeader>OTA Update Management (Legacy App)</PanelHeader>
      <PanelBody>
        <Row gutter={12} className="align-items-center mb-5 mt-3">
          <Col span={5}>
            <Select
              className="w-100"
              value={environmentFilter}
              onChange={setEnvironmentFilter}
            >
              {environmentOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={4}>
            <Select
              className="w-100"
              value={statusFilter}
              onChange={setStatusFilter}
            >
              {statusOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col span={15} className="text-right">
            <Button type="primary" onClick={() => setFormModalVisible(true)}>
              อัปโหลด version ใหม่
            </Button>
          </Col>
        </Row>

        <Table
          bordered
          size="small"
          rowKey="id"
          loading={isLoading}
          dataSource={versions}
          columns={columns}
          scroll={{ x: 'max-content' }}
          pagination={{
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จากทั้งหมด ${total} รายการ`,
            showLessItems: true,
            size: 'default',
          }}
        />
      </PanelBody>

      <OtaFormModal
        open={formModalVisible}
        user={user}
        onClose={() => setFormModalVisible(false)}
        onSuccess={() => {
          setFormModalVisible(false);
          refetch();
        }}
      />

      <FillBundleModal
        record={fillTarget}
        onClose={() => setFillTarget(null)}
        onSuccess={() => {
          setFillTarget(null);
          refetch();
        }}
      />

      <Modal
        visible={!!prodConfirmTarget}
        title="ยืนยันเปิดใช้งานบน production"
        okText="เปิดใช้งาน"
        okButtonProps={{
          danger: true,
          disabled: prodConfirmText !== 'CONFIRM',
        }}
        cancelText="ยกเลิก"
        zIndex={1080}
        onOk={() => {
          toggleActive({ id: prodConfirmTarget.record.id, active: true });
          setProdConfirmTarget(null);
        }}
        onCancel={() => setProdConfirmTarget(null)}
      >
        {prodConfirmTarget && (
          <>
            <p>
              app {prodConfirmTarget.record.targetAppVersion} build{' '}
              {prodConfirmTarget.record.minBuildNumber}
              {prodConfirmTarget.record.maxBuildNumber
                ? `-${prodConfirmTarget.record.maxBuildNumber}`
                : '+'}{' '}
              จะได้รับ OTA v{prodConfirmTarget.record.version}
              {prodConfirmTarget.warning}
            </p>
            <p>
              พิมพ์ <Text strong>CONFIRM</Text> เพื่อยืนยัน
            </p>
            <Input
              value={prodConfirmText}
              onChange={(e) => setProdConfirmText(e.target.value)}
              placeholder="CONFIRM"
            />
          </>
        )}
      </Modal>
    </Panel>
  );
};

export default LegacyOtaPage;
